// EditDayNoteModal — edit the body of an existing diary day note.
//
// Type is read-only after creation (matches the legacy behaviour — the
// note's type was set when it was added). Submit calls
// PUT /api/v1/collections/diary/notes/:enc_id via useDayNotes().

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { Badge } from '../../../components/ui/Badge/Badge';
import { useToast } from '../../../hooks/useToast';
import { useDayNotes } from '../hooks/useDayNotes';
import { cn } from '../../../utils/cn';
import type { DiaryNoteDto } from '../../../types/api';
import styles from './DayNoteModal.module.css';

const MAX_NOTE_LENGTH = 500;
const WARN_AT = 450;

export interface EditDayNoteModalProps {
  open: boolean;
  driverId: number;
  weekStart: string;
  note: DiaryNoteDto | null;
  onClose: () => void;
}

export function EditDayNoteModal({
  open,
  driverId,
  weekStart,
  note,
  onClose,
}: EditDayNoteModalProps): JSX.Element | null {
  const toast = useToast();
  const { updateNote } = useDayNotes({ driverId, weekStart });
  const [text, setText] = useState('');

  useEffect(() => {
    if (open && note) setText(note.note_text);
  }, [open, note?.enc_id, note?.note_text]);

  if (!open || !note) return null;

  const length = text.length;
  const overLimit = length > MAX_NOTE_LENGTH;
  const empty = text.trim().length === 0;
  const unchanged = text === note.note_text;
  const counterClass = cn(
    styles.counter,
    !overLimit && length >= WARN_AT && styles.counterWarning,
    overLimit && styles.counterError,
  );

  async function handleSubmit() {
    if (empty || overLimit || unchanged || !note) return;
    try {
      await updateNote.mutateAsync({ encId: note.enc_id, noteText: text });
      toast.success('Note updated');
      onClose();
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not update the note.'
        : 'Could not update the note.';
      toast.error(message);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Note"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateNote.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={updateNote.isPending}
            disabled={empty || overLimit || unchanged}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.typeReadonly}>
          <span className={styles.typeReadonlyLabel}>Type</span>
          <Badge variant={note.note_type === 'alert' ? 'danger' : 'default'}>
            {note.note_type === 'alert' ? 'Alert' : 'Note'}
          </Badge>
          <span className={styles.typeReadonlyHint}>
            (cannot be changed after creation)
          </span>
        </div>
        <div>
          <Textarea
            label="Note text"
            required
            autoResize
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Note text"
          />
          <span className={counterClass}>
            {length} / {MAX_NOTE_LENGTH}
          </span>
        </div>
      </div>
    </Modal>
  );
}
